export interface ClientOptions {
  baseUrl: string;
  token?: string;
  apiKey?: string;
  gatewayUser?: string;
  fetchImpl?: typeof fetch;
  credentials?: RequestCredentials;
  extraHeaders?: Record<string, string> | (() => Record<string, string>);
}

export interface FileEntry {
  id: string;
  owner_type: string;
  owner_id: string;
  parent_id?: string | null;
  name: string;
  path: string;
  is_directory: boolean;
  size: number;
  mime_type?: string | null;
  is_public: boolean;
  metadata_json?: Record<string, unknown> | null;
  tags?: string[];
  uploaded_by: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  etag?: string;
}

export interface FileList {
  items: FileEntry[];
  total: number;
}

export interface ListFilesOptions {
  ownerType?: string;
  ownerId?: string;
  parentId?: string | null;
  pathPrefix?: string;
  query?: string;
  mimeType?: string;
  dateFrom?: string;
  dateTo?: string;
  sizeMin?: number;
  sizeMax?: number;
  tags?: string[];
  tagMatch?: string;
  metadataKey?: string;
  metadataValue?: string;
  page?: number;
  pageSize?: number;
  /** 增量同步游标：只返回 updated_at >= 该时间的文件（RFC3339） */
  updatedSince?: string;
  /** 包含软删除文件，用于增量同步感知删除 */
  includeDeleted?: boolean;
}

export interface MkdirRequest {
  owner_type?: string;
  owner_id?: string;
  parent_id?: string | null;
  name: string;
  conflict_strategy?: string;
}

export interface UploadFileRequest {
  file: Blob | ArrayBuffer | Uint8Array | string;
  filename: string;
  contentType?: string;
  ownerType?: string;
  ownerId?: string;
  parentId?: string | null;
  isPublic?: boolean;
  metadataJson?: Record<string, unknown>;
  tags?: string[];
  conflictStrategy?: string;
  /** If-Match 乐观锁：传入上次拿到的 etag，服务端不匹配时返 412 */
  ifMatch?: string;
}

export interface FileVersion {
  id: string;
  file_id: string;
  version: number;
  size: number;
  mime_type: string;
  name: string;
  metadata_json?: Record<string, unknown>;
  created_by: string;
  created_at: string;
}

export interface FileVersionList {
  items: FileVersion[];
  total: number;
}

export interface FileVersionMutation {
  file: FileEntry;
  version: FileVersion;
}

export interface FileACL {
  file_id: string;
  target_type: string;
  target_id: string;
  permission: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface FileACLList {
  items: FileACL[];
}

export interface FilePermissionDecision {
  source: string;
  matched: boolean;
  decisive: boolean;
  permission: string;
  permission_level: number;
  reason: string;
  file_id?: string;
  file_name?: string;
  path?: string;
  target_type?: string;
  target_id?: string;
  is_inherited?: boolean;
}

export interface FilePermissionExplanation {
  file: FileEntry;
  user_id: string;
  groups?: string[];
  api_key?: boolean;
  effective_permission: string;
  effective_level: number;
  source: string;
  actions: Record<string, boolean>;
  decisions: FilePermissionDecision[];
}

export interface ExplainFilePermissionOptions {
  userId: string;
  groups?: string[];
  apiKey?: boolean;
}

export interface FileACLUpsertRequest {
  target_type: string;
  target_id: string;
  permission: string;
}

export interface UploadFileVersionRequest {
  file: Blob | ArrayBuffer | Uint8Array | string;
  filename: string;
  contentType?: string;
  metadataJson?: Record<string, unknown>;
}

export interface CreateShareRequest {
  password?: string;
  expires_in?: number;
  max_downloads?: number;
  recipient_type?: string;
  recipient_id?: string;
}

export interface ShareLink {
  id: string;
  file_id: string;
  token: string;
  has_password: boolean;
  expires_at?: string | null;
  download_count: number;
  max_downloads?: number | null;
  recipient_type?: string | null;
  recipient_id?: string | null;
  created_by: string;
  created_at: string;
  revoked_at?: string | null;
}

export interface DeployStaticSiteRequest {
  owner_type?: string;
  owner_id?: string;
  slug: string;
  domain?: string | null;
  source_type?: string;
  source_file_id?: string | null;
  render_type?: string;
  theme?: string;
  environment?: string;
  index_file?: string;
  spa_fallback?: boolean;
  cache_max_age?: number;
  security_headers?: Record<string, unknown>;
}

export interface StaticSite {
  id: string;
  owner_type: string;
  owner_id: string;
  root_file_id: string;
  slug: string;
  domain?: string | null;
  index_file: string;
  spa_fallback: boolean;
  enabled: boolean;
  cache_max_age: number;
  cache_revision: string;
  render_type: string;
  theme?: string;
  environment: string;
  source_type: string;
  source_file_id?: string | null;
  render_status: string;
  render_error?: string | null;
  published_at?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface StaticSiteDeployment {
  id: string;
  site_id: string;
  owner_type: string;
  owner_id: string;
  trigger: string;
  source_type: string;
  source_file_id?: string | null;
  root_file_id?: string | null;
  render_type: string;
  theme?: string;
  environment: string;
  status: string;
  error?: string | null;
  duration_ms?: number | null;
  triggered_by: string;
  started_at?: string | null;
  finished_at?: string | null;
  created_at: string;
}

export interface StaticSiteDeploymentList {
  items: StaticSiteDeployment[];
  total: number;
}

export interface StaticSiteRollback {
  site: StaticSite;
  deployment: StaticSiteDeployment;
}

export interface CreateStaticSiteDomainRequest {
  domain: string;
}

export interface StaticSiteDomain {
  id: string;
  site_id: string;
  owner_type: string;
  owner_id: string;
  domain: string;
  status: string;
  verification_method: string;
  verification_dns_name: string;
  verification_dns_value: string;
  last_verification_error?: string | null;
  certificate_status: string;
  created_by: string;
  verified_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface StaticSiteDomainList {
  items: StaticSiteDomain[];
  total: number;
}

export interface AuditLog {
  id: string;
  owner_type: string;
  owner_id: string;
  actor_id: string;
  actor_type: string;
  event: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
  ip_address: string;
  user_agent: string;
  metadata_json?: Record<string, unknown>;
  created_at: string;
}

export interface AuditLogList {
  items: AuditLog[];
  total: number;
}

export interface AuditAlert {
	event: string;
	owner_type: string;
	owner_id: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
  count: number;
  first_seen_at: string;
  last_seen_at: string;
	last_actor_id: string;
	last_actor_type: string;
	last_metadata_json?: Record<string, unknown>;
	alert_key: string;
	status: string;
	acknowledged_by?: string;
	acknowledged_at?: string | null;
	silence_until?: string | null;
	note?: string;
	last_notified_at?: string | null;
}

export interface AuditAlertList {
	items: AuditAlert[];
	total: number;
}

export interface AuditAlertPolicy {
	id: string;
	owner_type: string;
	owner_id: string;
	event: string;
	min_count: number;
	window_seconds: number;
	cooldown_seconds: number;
	enabled: boolean;
	created_by: string;
	created_at: string;
	updated_at: string;
}

export interface AuditAlertPolicyList {
	items: AuditAlertPolicy[];
	total: number;
}

export interface AuditAlertPolicyRequest {
	owner_type: string;
	owner_id: string;
	event: string;
	min_count: number;
	window_seconds: number;
	cooldown_seconds: number;
	enabled?: boolean;
}

export interface AuditAlertState {
	alert_key: string;
	event: string;
	owner_type: string;
	owner_id: string;
	resource_type: string;
	resource_id: string;
	status: string;
	acknowledged_by?: string;
	acknowledged_at?: string | null;
	silence_until?: string | null;
	note?: string;
	last_notified_at?: string | null;
	created_at: string;
	updated_at: string;
}

export interface AuditAlertStateRequest {
	event: string;
	owner_type: string;
	owner_id: string;
	resource_type: string;
	resource_id: string;
	status: string;
	silence_until?: string;
	note?: string;
}

export interface ListAuditLogsOptions {
  ownerType?: string;
  ownerId?: string;
  actor?: string;
  event?: string;
  resourceType?: string;
  resourceId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface ListAuditAlertsOptions {
	ownerType?: string;
  ownerId?: string;
  event?: string;
  resourceType?: string;
  resourceId?: string;
  dateFrom?: string;
  dateTo?: string;
  minCount?: number;
  page?: number;
	pageSize?: number;
}

export interface ListAuditAlertPoliciesOptions {
	ownerType?: string;
	ownerId?: string;
	event?: string;
	page?: number;
	pageSize?: number;
}

export interface WebhookSubscription {
  id: string;
  owner_type: string;
  owner_id: string;
  url: string;
  secret?: string;
  events: string[];
  enabled: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface WebhookSubscriptionList {
  items: WebhookSubscription[];
  total: number;
}

export interface CreateWebhookRequest {
  owner_type?: string;
  owner_id?: string;
  url: string;
  secret?: string;
  events?: string[];
  event_templates?: string[];
}

export interface UpdateWebhookRequest {
  url?: string;
  secret?: string;
  events?: string[];
  event_templates?: string[];
  enabled?: boolean;
}

export interface ListWebhooksOptions {
  ownerType?: string;
  ownerId?: string;
  page?: number;
  pageSize?: number;
}

export interface WebhookDelivery {
  id: string;
  subscription_id: string;
  owner_type: string;
  owner_id: string;
  event: string;
  resource_type: string;
  resource_id: string;
  payload_json: Record<string, unknown>;
  status: string;
  attempts: number;
  max_attempts: number;
  next_attempt_at?: string | null;
  last_error?: string | null;
  delivered_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebhookDeliveryList {
  items: WebhookDelivery[];
  total: number;
}

export interface WebhookEventCatalog {
  categories: Array<{ id: string; name: string; events: string[] }>;
  templates: Array<{ id: string; name: string; description: string; events: string[] }>;
  events: string[];
}

export interface PageOptions {
  page?: number;
  pageSize?: number;
}

export class UFSApiError extends Error {
  status: number;
  body: string;

  constructor(status: number, message: string, body = "") {
    super(`ufs: status ${status}: ${message}`);
    this.name = "UFSApiError";
    this.status = status;
    this.body = body;
  }
}

export class UnifiedFileServiceClient {
  baseUrl: string;
  token?: string;
  apiKey?: string;
  gatewayUser?: string;
  fetchImpl: typeof fetch;
  credentials?: RequestCredentials;
  extraHeaders?: () => Record<string, string>;

  constructor(options: ClientOptions) {
    if (!options.baseUrl || !options.baseUrl.trim()) {
      throw new Error("baseUrl is required");
    }
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.token = options.token;
    this.apiKey = options.apiKey;
    this.gatewayUser = options.gatewayUser;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.credentials = options.credentials;
    this.extraHeaders =
      typeof options.extraHeaders === "function"
        ? options.extraHeaders
        : options.extraHeaders
        ? () => options.extraHeaders as Record<string, string>
        : undefined;
  }

  listFiles(options: ListFilesOptions = {}): Promise<FileList> {
    const query = new URLSearchParams();
    append(query, "owner_type", options.ownerType);
    append(query, "owner_id", options.ownerId);
    if (options.parentId !== undefined) query.set("parent_id", options.parentId ?? "");
    append(query, "path_prefix", options.pathPrefix);
    append(query, "q", options.query);
    append(query, "mime", options.mimeType);
    append(query, "date_from", options.dateFrom);
    append(query, "date_to", options.dateTo);
    append(query, "size_min", options.sizeMin);
    append(query, "size_max", options.sizeMax);
    for (const tag of options.tags ?? []) append(query, "tag", tag);
    append(query, "tag_match", options.tagMatch);
    append(query, "metadata_key", options.metadataKey);
    append(query, "metadata_value", options.metadataValue);
    append(query, "page", options.page);
    append(query, "page_size", options.pageSize);
    append(query, "updated_since", options.updatedSince);
    if (options.includeDeleted) query.set("include_deleted", "true");
    return this.requestJson<FileList>("GET", "/api/files", { query });
  }

  createDirectory(request: MkdirRequest): Promise<FileEntry> {
    return this.requestJson<FileEntry>("POST", "/api/files/mkdir", { body: request });
  }

  uploadFile(request: UploadFileRequest): Promise<FileEntry> {
    const form = new FormData();
    form.append("file", toBlob(request.file, request.contentType), request.filename);
    appendForm(form, "owner_type", request.ownerType);
    appendForm(form, "owner_id", request.ownerId);
    if (request.parentId !== undefined && request.parentId !== null) form.append("parent_id", request.parentId);
    if (request.isPublic) form.append("is_public", "true");
    if (request.metadataJson) form.append("metadata_json", JSON.stringify(request.metadataJson));
    for (const tag of request.tags ?? []) form.append("tag", tag);
    appendForm(form, "conflict_strategy", request.conflictStrategy);
    const headers: Record<string, string> = {};
    if (request.ifMatch) headers["If-Match"] = request.ifMatch;
    return this.requestJson<FileEntry>("POST", "/api/files/upload", { body: form, headers });
  }

  downloadFile(fileId: string, options?: { ifNoneMatch?: string }): Promise<Response> {
    requireValue(fileId, "fileId");
    const headers = options?.ifNoneMatch ? { "If-None-Match": options.ifNoneMatch } : undefined;
    return this.requestResponse("GET", `/api/files/${encodeURIComponent(fileId)}/download`, { headers });
  }

  listFileVersions(fileId: string): Promise<FileVersionList> {
    requireValue(fileId, "fileId");
    return this.requestJson<FileVersionList>("GET", `/api/files/${encodeURIComponent(fileId)}/versions`);
  }

  uploadFileVersion(fileId: string, request: UploadFileVersionRequest): Promise<FileVersionMutation> {
    requireValue(fileId, "fileId");
    const form = new FormData();
    form.append("file", toBlob(request.file, request.contentType), request.filename);
    if (request.metadataJson) form.append("metadata_json", JSON.stringify(request.metadataJson));
    return this.requestJson<FileVersionMutation>("POST", `/api/files/${encodeURIComponent(fileId)}/versions`, { body: form });
  }

  downloadFileVersion(fileId: string, version: number): Promise<Response> {
    requireValue(fileId, "fileId");
    return this.requestResponse("GET", `/api/files/${encodeURIComponent(fileId)}/versions/${version}/download`);
  }

  restoreFileVersion(fileId: string, version: number): Promise<FileVersionMutation> {
    requireValue(fileId, "fileId");
    return this.requestJson<FileVersionMutation>("POST", `/api/files/${encodeURIComponent(fileId)}/versions/${version}/restore`);
  }

  listFileAcl(fileId: string): Promise<FileACLList> {
    requireValue(fileId, "fileId");
    return this.requestJson<FileACLList>("GET", `/api/files/${encodeURIComponent(fileId)}/acl`);
  }

  upsertFileAcl(fileId: string, request: FileACLUpsertRequest): Promise<FileACL> {
    requireValue(fileId, "fileId");
    return this.requestJson<FileACL>("PUT", `/api/files/${encodeURIComponent(fileId)}/acl`, { body: request });
  }

  deleteFileAcl(fileId: string, targetType: string, targetId: string): Promise<void> {
    requireValue(fileId, "fileId");
    requireValue(targetType, "targetType");
    requireValue(targetId, "targetId");
    return this.requestVoid(
      "DELETE",
      `/api/files/${encodeURIComponent(fileId)}/acl/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`,
    );
  }

  explainFilePermission(fileId: string, options: ExplainFilePermissionOptions): Promise<FilePermissionExplanation> {
    requireValue(fileId, "fileId");
    requireValue(options.userId, "userId");
    const query = new URLSearchParams();
    append(query, "user_id", options.userId);
    const groups = (options.groups || []).map(group => group.trim()).filter(Boolean);
    if (groups.length) append(query, "groups", groups.join(","));
    if (options.apiKey) append(query, "api_key", "true");
    return this.requestJson<FilePermissionExplanation>("GET", `/api/files/${encodeURIComponent(fileId)}/permissions/explain`, { query });
  }

  createShare(fileId: string, request: CreateShareRequest): Promise<ShareLink> {
    requireValue(fileId, "fileId");
    return this.requestJson<ShareLink>("POST", `/api/files/${encodeURIComponent(fileId)}/share`, { body: request });
  }

  listFileShares(fileId: string): Promise<ShareLink[]> {
    requireValue(fileId, "fileId");
    return this.requestJson<ShareLink[]>("GET", `/api/files/${encodeURIComponent(fileId)}/shares`);
  }

  revokeShare(shareId: string): Promise<void> {
    requireValue(shareId, "shareId");
    return this.requestVoid("DELETE", `/api/shares/${encodeURIComponent(shareId)}`);
  }

  getShare(token: string): Promise<ShareLink> {
    requireValue(token, "token");
    return this.requestJson<ShareLink>("GET", `/share/${encodeURIComponent(token)}`);
  }

  getShareAuthenticated(token: string): Promise<ShareLink> {
    requireValue(token, "token");
    return this.requestJson<ShareLink>("GET", `/api/share/${encodeURIComponent(token)}`);
  }

  downloadShare(token: string, password?: string): Promise<Response> {
    return this.shareContent(token, "download", password, false);
  }

  previewShare(token: string, password?: string): Promise<Response> {
    return this.shareContent(token, "preview", password, false);
  }

  downloadShareAuthenticated(token: string, password?: string): Promise<Response> {
    return this.shareContent(token, "download", password, true);
  }

  previewShareAuthenticated(token: string, password?: string): Promise<Response> {
    return this.shareContent(token, "preview", password, true);
  }

  deployStaticSite(request: DeployStaticSiteRequest): Promise<StaticSite> {
    return this.requestJson<StaticSite>("POST", "/api/static-sites/deploy", { body: request });
  }

  getStaticSite(siteId: string): Promise<StaticSite> {
    requireValue(siteId, "siteId");
    return this.requestJson<StaticSite>("GET", `/api/static-sites/${encodeURIComponent(siteId)}`);
  }

  listStaticSiteDeployments(siteId: string, options: PageOptions = {}): Promise<StaticSiteDeploymentList> {
    requireValue(siteId, "siteId");
    const query = pageQuery(options);
    return this.requestJson<StaticSiteDeploymentList>("GET", `/api/static-sites/${encodeURIComponent(siteId)}/deployments`, { query });
  }

  rollbackStaticSite(siteId: string, deploymentId: string): Promise<StaticSiteRollback> {
    requireValue(siteId, "siteId");
    requireValue(deploymentId, "deploymentId");
    return this.requestJson<StaticSiteRollback>(
      "POST",
      `/api/static-sites/${encodeURIComponent(siteId)}/deployments/${encodeURIComponent(deploymentId)}/rollback`,
    );
  }

  createStaticSiteDomain(siteId: string, request: CreateStaticSiteDomainRequest): Promise<StaticSiteDomain> {
    requireValue(siteId, "siteId");
    return this.requestJson<StaticSiteDomain>("POST", `/api/static-sites/${encodeURIComponent(siteId)}/domains`, { body: request });
  }

  listStaticSiteDomains(siteId: string): Promise<StaticSiteDomainList> {
    requireValue(siteId, "siteId");
    return this.requestJson<StaticSiteDomainList>("GET", `/api/static-sites/${encodeURIComponent(siteId)}/domains`);
  }

  verifyStaticSiteDomain(siteId: string, domainId: string): Promise<StaticSiteDomain> {
    requireValue(siteId, "siteId");
    requireValue(domainId, "domainId");
    return this.requestJson<StaticSiteDomain>(
      "POST",
      `/api/static-sites/${encodeURIComponent(siteId)}/domains/${encodeURIComponent(domainId)}/verify`,
    );
  }

  deleteStaticSiteDomain(siteId: string, domainId: string): Promise<void> {
    requireValue(siteId, "siteId");
    requireValue(domainId, "domainId");
    return this.requestVoid("DELETE", `/api/static-sites/${encodeURIComponent(siteId)}/domains/${encodeURIComponent(domainId)}`);
  }

  listAuditLogs(options: ListAuditLogsOptions = {}): Promise<AuditLogList> {
    const query = new URLSearchParams();
    append(query, "owner_type", options.ownerType);
    append(query, "owner_id", options.ownerId);
    append(query, "actor", options.actor);
    append(query, "event", options.event);
    append(query, "resource_type", options.resourceType);
    append(query, "resource_id", options.resourceId);
    append(query, "date_from", options.dateFrom);
    append(query, "date_to", options.dateTo);
    append(query, "page", options.page);
    append(query, "page_size", options.pageSize);
    return this.requestJson<AuditLogList>("GET", "/api/audit-logs", { query });
  }

	listAuditAlerts(options: ListAuditAlertsOptions = {}): Promise<AuditAlertList> {
		const query = new URLSearchParams();
    append(query, "owner_type", options.ownerType);
    append(query, "owner_id", options.ownerId);
    append(query, "event", options.event);
    append(query, "resource_type", options.resourceType);
    append(query, "resource_id", options.resourceId);
    append(query, "date_from", options.dateFrom);
    append(query, "date_to", options.dateTo);
    append(query, "min_count", options.minCount);
    append(query, "page", options.page);
    append(query, "page_size", options.pageSize);
		return this.requestJson<AuditAlertList>("GET", "/api/audit-logs/alerts", { query });
	}

	createAuditAlertPolicy(request: AuditAlertPolicyRequest): Promise<AuditAlertPolicy> {
		return this.requestJson<AuditAlertPolicy>("POST", "/api/audit-logs/alert-policies", { body: request });
	}

	listAuditAlertPolicies(options: ListAuditAlertPoliciesOptions = {}): Promise<AuditAlertPolicyList> {
		const query = new URLSearchParams();
		append(query, "owner_type", options.ownerType);
		append(query, "owner_id", options.ownerId);
		append(query, "event", options.event);
		append(query, "page", options.page);
		append(query, "page_size", options.pageSize);
		return this.requestJson<AuditAlertPolicyList>("GET", "/api/audit-logs/alert-policies", { query });
	}

	updateAuditAlertPolicy(policyId: string, request: AuditAlertPolicyRequest): Promise<AuditAlertPolicy> {
		requireValue(policyId, "policyId");
		return this.requestJson<AuditAlertPolicy>(
			"PUT",
			`/api/audit-logs/alert-policies/${encodeURIComponent(policyId)}`,
			{ body: request },
		);
	}

	deleteAuditAlertPolicy(policyId: string): Promise<void> {
		requireValue(policyId, "policyId");
		return this.requestVoid("DELETE", `/api/audit-logs/alert-policies/${encodeURIComponent(policyId)}`);
	}

	setAuditAlertState(request: AuditAlertStateRequest): Promise<AuditAlertState> {
		return this.requestJson<AuditAlertState>("PUT", "/api/audit-logs/alert-states", { body: request });
	}

	getWebhookCatalog(): Promise<WebhookEventCatalog> {
		return this.requestJson<WebhookEventCatalog>("GET", "/api/webhooks/catalog");
	}

  createWebhook(request: CreateWebhookRequest): Promise<WebhookSubscription> {
    return this.requestJson<WebhookSubscription>("POST", "/api/webhooks", { body: request });
  }

  listWebhooks(options: ListWebhooksOptions = {}): Promise<WebhookSubscriptionList> {
    const query = new URLSearchParams();
    append(query, "owner_type", options.ownerType);
    append(query, "owner_id", options.ownerId);
    append(query, "page", options.page);
    append(query, "page_size", options.pageSize);
    return this.requestJson<WebhookSubscriptionList>("GET", "/api/webhooks", { query });
  }

  updateWebhook(webhookId: string, request: UpdateWebhookRequest): Promise<WebhookSubscription> {
    requireValue(webhookId, "webhookId");
    return this.requestJson<WebhookSubscription>("PUT", `/api/webhooks/${encodeURIComponent(webhookId)}`, { body: request });
  }

  deleteWebhook(webhookId: string): Promise<void> {
    requireValue(webhookId, "webhookId");
    return this.requestVoid("DELETE", `/api/webhooks/${encodeURIComponent(webhookId)}`);
  }

  listWebhookDeliveries(webhookId: string, options: PageOptions = {}): Promise<WebhookDeliveryList> {
    requireValue(webhookId, "webhookId");
    const query = pageQuery(options);
    return this.requestJson<WebhookDeliveryList>("GET", `/api/webhooks/${encodeURIComponent(webhookId)}/deliveries`, { query });
  }

  shareContent(token: string, action: "download" | "preview", password?: string, authenticated = false): Promise<Response> {
    requireValue(token, "token");
    const query = new URLSearchParams();
    append(query, "password", password);
    const prefix = authenticated ? "/api/share" : "/share";
    return this.requestResponse("GET", `${prefix}/${encodeURIComponent(token)}/${action}`, { query });
  }

  async requestJson<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.requestResponse(method, path, options);
    return response.json() as Promise<T>;
  }

  async requestVoid(method: string, path: string, options: RequestOptions = {}): Promise<void> {
    const response = await this.requestResponse(method, path, options);
    await response.arrayBuffer();
  }

  async requestResponse(method: string, path: string, options: RequestOptions = {}): Promise<Response> {
    const url = new URL(this.baseUrl + path);
    if (options.query) {
      for (const [key, value] of options.query.entries()) {
        url.searchParams.append(key, value);
      }
    }

    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    if (this.extraHeaders) {
      for (const [key, value] of Object.entries(this.extraHeaders())) {
        headers.set(key, value);
      }
    }
    if (this.gatewayUser) headers.set("X-User-ID", this.gatewayUser);
    if (this.apiKey) headers.set("X-API-Key", this.apiKey);
    if (this.token) headers.set("Authorization", `Bearer ${this.token}`);

    let body: BodyInit | undefined = options.body as BodyInit | undefined;
    if (body !== undefined && !(body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(body);
    }

    const response = await this.fetchImpl(url, {
      method,
      headers,
      body,
      ...(this.credentials !== undefined ? { credentials: this.credentials } : {}),
    });
    // 304 是条件请求的正常响应，不应抛错；其他非 2xx 才是错误
    if (!response.ok && response.status !== 304) {
      throw await buildApiError(response);
    }
    return response;
  }
}

interface RequestOptions {
  query?: URLSearchParams;
  body?: unknown;
  headers?: HeadersInit;
}

function append(query: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null || value === "") return;
  query.append(key, String(value));
}

function appendForm(form: FormData, key: string, value: unknown): void {
  if (value === undefined || value === null || value === "") return;
  form.append(key, String(value));
}

function pageQuery(options: PageOptions): URLSearchParams {
  const query = new URLSearchParams();
  append(query, "page", options.page);
  append(query, "page_size", options.pageSize);
  return query;
}

function toBlob(file: Blob | ArrayBuffer | Uint8Array | string, contentType = "application/octet-stream"): Blob {
  if (file instanceof Blob) return file;
  return new Blob([file as BlobPart], { type: contentType });
}

function requireValue(value: string, name: string): void {
  if (!value || !value.trim()) {
    throw new Error(`${name} is required`);
  }
}

async function buildApiError(response: Response): Promise<UFSApiError> {
  const body = await response.text();
  let message = body || response.statusText;
  try {
    const payload = JSON.parse(body);
    if (payload && typeof payload.error === "string") {
      message = payload.error;
    }
  } catch {
    // 非 JSON 错误体保持原始文本，便于排障。
  }
  return new UFSApiError(response.status, message, body);
}
